import { useState, useEffect } from "react";

function getInitials(displayName, email) {
  const src = displayName || email || "?";
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src[0].toUpperCase();
}

export function UserSettingsPage({ user, theme, onThemeChange, fontSize, onFontSizeChange, onNavigate, onLogout, onUserUpdate, pcapSession, collapsed, onToggleCollapse, ui }) {
  const { B, fs, HP, AppShell } = ui;
  const [section, setSection] = useState("profile");

  // Profile
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [nameStatus, setNameStatus] = useState(null);
  const [nameLoading, setNameLoading] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);

  // Security / password
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwStatus, setPwStatus] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  // Delete account
  const [deletePhase, setDeletePhase] = useState("idle"); // idle | confirm | deleting
  const [deletePw, setDeletePw] = useState("");
  const [deleteError, setDeleteError] = useState(null);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactEmail, setContactEmail] = useState("");
  const [contactStatus, setContactStatus] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    if (section === "contacts") loadContacts();
  }, [section]);

  const loadContacts = async () => {
    try {
      const res = await fetch("/api/contacts", { credentials: "include" });
      const data = await res.json();
      if (res.ok) setContacts(data.contacts || []);
    } catch (_) {}
  };

  const S = {
    page: { background: B.bg, minHeight: "100vh", fontFamily: "'Satoshi', sans-serif", color: B.text },
    shell: { maxWidth: 820, margin: "0 auto", padding: "32px 24px 80px" },
    layout: { display: "grid", gridTemplateColumns: "180px 1fr", gap: 32, alignItems: "start" },
    sidebar: { position: "sticky", top: 80 },
    navItem: (active) => ({
      display: "block", width: "100%", textAlign: "left", padding: "9px 14px", borderRadius: 8,
      background: active ? `${B.primary}18` : "none", border: active ? `1px solid ${B.primary}40` : "1px solid transparent",
      color: active ? B.primary : B.textMuted, fontWeight: active ? 800 : 500,
      fontSize: fs(12), cursor: "pointer", fontFamily: "'Satoshi', sans-serif", marginBottom: 2,
    }),
    navSection: { fontSize: fs(9), fontWeight: 800, color: B.textDim, textTransform: "uppercase", letterSpacing: 1.4, padding: "12px 14px 4px" },
    card: { background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 24, marginBottom: 16 },
    dangerCard: { background: "#ef444408", border: "1px solid #ef444430", borderRadius: 12, padding: 24, marginBottom: 16 },
    label: { fontSize: fs(10), color: B.textDim, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 },
    sectionTitle: { fontSize: fs(16), fontWeight: 900, color: B.text, marginBottom: 4 },
    sectionSub: { fontSize: fs(12), color: B.textMuted, marginBottom: 20 },
    input: { width: "100%", padding: "9px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", fontSize: fs(13), outline: "none", boxSizing: "border-box" },
    chipGroup: { display: "flex", gap: 8 },
    chip: (active) => ({
      padding: "6px 16px", borderRadius: 999, fontSize: fs(11), fontWeight: 700, cursor: "pointer", fontFamily: "'Satoshi', sans-serif",
      background: active ? B.primary : B.bgPanel, color: active ? "#fff" : B.textMuted,
      border: `1px solid ${active ? B.primary : B.border}`,
    }),
    statusBox: (ok) => ({ fontSize: fs(12), color: ok ? "#22c55e" : "#ef4444", padding: "8px 12px", borderRadius: 8, background: ok ? "#22c55e14" : "#ef444414", border: `1px solid ${ok ? "#22c55e40" : "#ef444440"}` }),
  };

  const handleSaveName = async () => {
    setNameStatus(null);
    setNameLoading(true);
    try {
      const res = await fetch("/api/account/profile", {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ displayName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save.");
      setNameStatus({ ok: true, msg: "Display name updated." });
      if (onUserUpdate) onUserUpdate({ ...user, displayName: data.displayName });
    } catch (e) {
      setNameStatus({ ok: false, msg: e.message });
    } finally {
      setNameLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPwStatus(null);
    if (newPw !== confirmPw) { setPwStatus({ ok: false, msg: "New passwords don't match." }); return; }
    if (newPw.length < 8) { setPwStatus({ ok: false, msg: "Password must be at least 8 characters." }); return; }
    setPwLoading(true);
    try {
      const res = await fetch("/api/account/password", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update password.");
      setPwStatus({ ok: true, msg: "Password updated successfully." });
      setCurPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      setPwStatus({ ok: false, msg: e.message });
    } finally {
      setPwLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    if (!deletePw) { setDeleteError("Password is required."); return; }
    setDeletePhase("deleting");
    try {
      const res = await fetch("/api/account", {
        method: "DELETE", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: deletePw }),
      });
      const data = await res.json();
      if (!res.ok) { setDeletePhase("confirm"); throw new Error(data.error || "Could not delete account."); }
      onLogout();
    } catch (e) {
      setDeleteError(e.message);
      setDeletePhase("confirm");
    }
  };

  const handleAddContact = async () => {
    setContactStatus(null);
    if (!contactEmail.trim()) return;
    setContactLoading(true);
    try {
      const res = await fetch("/api/contacts", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: contactEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not add contact.");
      setContacts(prev => [...prev, data.contact]);
      setContactEmail("");
      setContactStatus({ ok: true, msg: `${data.contact.contactEmail} added.` });
    } catch (e) {
      setContactStatus({ ok: false, msg: e.message });
    } finally {
      setContactLoading(false);
    }
  };

  const handleRemoveContact = async (id) => {
    try {
      await fetch(`/api/contacts/${id}`, { method: "DELETE", credentials: "include" });
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (_) {}
  };

  const FONT_OPTIONS = [
    { key: "compact", label: "Compact", scale: 1.0 },
    { key: "default", label: "Default", scale: 1.1 },
    { key: "large", label: "Large", scale: 1.2 },
  ];

  const THEME_OPTIONS = [
    { key: "dark", label: "Dark" },
    { key: "light", label: "Light" },
  ];

  const initials = getInitials(user?.displayName, user?.email);
  const maskedEmail = user?.email ? user.email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + "•".repeat(Math.min(b.length, 6)) + c) : "";

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="settings" onNavigate={onNavigate} user={user} onLogin={() => onNavigate("login")} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ ...S.page, flex: 1, minHeight: 0, overflowY: "auto" }}>
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>

      <div style={S.shell}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: fs(24), fontWeight: 900, color: B.text }}>Account Settings</div>
        </div>

        <div style={S.layout}>
          <nav style={S.sidebar}>
            <div style={S.navSection}>Account</div>
            {[
              { key: "profile", label: "Profile" },
              { key: "appearance", label: "Appearance" },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setSection(key)} style={S.navItem(section === key)}>{label}</button>
            ))}
            <div style={S.navSection}>Security</div>
            <button onClick={() => setSection("security")} style={S.navItem(section === "security")}>Password</button>
            <div style={S.navSection}>Social</div>
            <button onClick={() => setSection("contacts")} style={S.navItem(section === "contacts")}>Trusted Analysts</button>
          </nav>

          <div>
            {section === "profile" && (
              <>
                {/* Avatar card */}
                <div style={{ ...S.card, display: "flex", alignItems: "center", gap: 20, marginBottom: 16 }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: fs(26), fontWeight: 900, color: "#fff", flexShrink: 0 }}>
                    {initials}
                  </div>
                  <div>
                    <div style={{ fontSize: fs(18), fontWeight: 900, color: B.text }}>{user?.displayName || user?.email}</div>
                    <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 2 }}>
                      {emailRevealed ? user?.email : maskedEmail}
                      {" "}
                      <span onClick={() => setEmailRevealed(r => !r)} style={{ color: B.primary, cursor: "pointer", fontSize: fs(11) }}>
                        {emailRevealed ? "hide" : "reveal"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Display name */}
                <div style={S.card}>
                  <div style={S.sectionTitle}>Display Name</div>
                  <div style={S.sectionSub}>This is how you appear to other analysts.</div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <input
                      type="text"
                      value={displayName}
                      onChange={e => { setDisplayName(e.target.value); setNameStatus(null); }}
                      placeholder={user?.email || "Your name"}
                      maxLength={64}
                      style={S.input}
                      onKeyDown={e => e.key === "Enter" && handleSaveName()}
                    />
                    {nameStatus && <div style={S.statusBox(nameStatus.ok)}>{nameStatus.msg}</div>}
                    <button
                      onClick={handleSaveName}
                      disabled={nameLoading}
                      style={{ ...HP.primaryBtn, opacity: nameLoading ? 0.5 : 1, cursor: nameLoading ? "not-allowed" : "pointer", alignSelf: "start" }}
                    >
                      {nameLoading ? "Saving..." : "Save Name"}
                    </button>
                  </div>
                </div>
              </>
            )}

            {section === "appearance" && (
              <>
                <div style={S.card}>
                  <div style={S.sectionTitle}>Theme</div>
                  <div style={S.sectionSub}>Choose between dark and light interface.</div>
                  <div style={S.chipGroup}>
                    {THEME_OPTIONS.map(opt => (
                      <button key={opt.key} onClick={() => onThemeChange(opt.key)} style={S.chip(theme === opt.key)}>{opt.label}</button>
                    ))}
                  </div>
                </div>
                <div style={S.card}>
                  <div style={S.sectionTitle}>Font Size</div>
                  <div style={S.sectionSub}>Adjust the interface text size to your preference.</div>
                  <div style={S.chipGroup}>
                    {FONT_OPTIONS.map(opt => (
                      <button key={opt.key} onClick={() => onFontSizeChange(opt.scale)} style={S.chip(fontSize === opt.scale)}>{opt.label}</button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {section === "security" && (
              <>
                <div style={S.card}>
                  <div style={S.sectionTitle}>Change Password</div>
                  <div style={S.sectionSub}>Use a strong password that you don't use elsewhere.</div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div>
                      <div style={S.label}>Current Password</div>
                      <input type="password" value={curPw} onChange={e => setCurPw(e.target.value)} placeholder="••••••••" style={S.input} />
                    </div>
                    <div>
                      <div style={S.label}>New Password</div>
                      <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="••••••••" style={S.input} />
                    </div>
                    <div>
                      <div style={S.label}>Confirm New Password</div>
                      <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="••••••••" style={S.input} onKeyDown={e => e.key === "Enter" && handleChangePassword()} />
                    </div>
                    {pwStatus && <div style={S.statusBox(pwStatus.ok)}>{pwStatus.msg}</div>}
                    <button
                      onClick={handleChangePassword}
                      disabled={pwLoading || !curPw || !newPw || !confirmPw}
                      style={{ ...HP.primaryBtn, opacity: pwLoading || !curPw || !newPw || !confirmPw ? 0.5 : 1, cursor: pwLoading || !curPw || !newPw || !confirmPw ? "not-allowed" : "pointer" }}
                    >
                      {pwLoading ? "Updating..." : "Update Password"}
                    </button>
                  </div>
                </div>

                <div style={S.dangerCard}>
                  <div style={{ fontSize: fs(16), fontWeight: 900, color: "#ef4444", marginBottom: 4 }}>Delete Account</div>
                  <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 16 }}>Permanently delete your account and all associated data. This cannot be undone.</div>
                  {deletePhase === "idle" && (
                    <button onClick={() => setDeletePhase("confirm")} style={{ padding: "8px 20px", borderRadius: 8, background: "none", border: "1px solid #ef4444", color: "#ef4444", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: fs(13), cursor: "pointer" }}>
                      Delete My Account
                    </button>
                  )}
                  {(deletePhase === "confirm" || deletePhase === "deleting") && (
                    <div style={{ display: "grid", gap: 10 }}>
                      <div style={{ fontSize: fs(12), color: "#ef4444", fontWeight: 700 }}>Enter your password to confirm deletion:</div>
                      <input type="password" value={deletePw} onChange={e => setDeletePw(e.target.value)} placeholder="••••••••" style={{ ...S.input, border: "1px solid #ef444460" }} onKeyDown={e => e.key === "Enter" && handleDeleteAccount()} />
                      {deleteError && <div style={S.statusBox(false)}>{deleteError}</div>}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={handleDeleteAccount} disabled={deletePhase === "deleting" || !deletePw} style={{ padding: "8px 20px", borderRadius: 8, background: "#ef4444", border: "none", color: "#fff", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: fs(13), cursor: deletePhase === "deleting" || !deletePw ? "not-allowed" : "pointer", opacity: deletePhase === "deleting" || !deletePw ? 0.6 : 1 }}>
                          {deletePhase === "deleting" ? "Deleting..." : "Confirm Delete"}
                        </button>
                        <button onClick={() => { setDeletePhase("idle"); setDeletePw(""); setDeleteError(null); }} style={{ padding: "8px 20px", borderRadius: 8, background: "none", border: `1px solid ${B.border}`, color: B.textMuted, fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: fs(13), cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {section === "contacts" && (
              <div style={S.card}>
                <div style={S.sectionTitle}>Trusted Analysts</div>
                <div style={S.sectionSub}>Add colleagues by their Idle Hour email. They'll appear as quick-select options when sharing investigations.</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={e => { setContactEmail(e.target.value); setContactStatus(null); }}
                    placeholder="analyst@example.com"
                    style={{ ...S.input, marginBottom: 0 }}
                    onKeyDown={e => e.key === "Enter" && handleAddContact()}
                  />
                  <button onClick={handleAddContact} disabled={contactLoading || !contactEmail.trim()} style={{ ...HP.primaryBtn, whiteSpace: "nowrap", opacity: contactLoading || !contactEmail.trim() ? 0.5 : 1, cursor: contactLoading || !contactEmail.trim() ? "not-allowed" : "pointer" }}>
                    {contactLoading ? "Adding..." : "Add"}
                  </button>
                </div>
                {contactStatus && <div style={{ ...S.statusBox(contactStatus.ok), marginBottom: 16 }}>{contactStatus.msg}</div>}
                {contacts.length === 0 ? (
                  <div style={{ color: B.textMuted, fontSize: fs(13), padding: "20px 0", textAlign: "center" }}>No trusted analysts yet. Add someone above to get started.</div>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {contacts.map(c => (
                      <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}` }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: fs(13), color: B.text }}>{c.contactEmail}</div>
                          <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 2 }}>Added {new Date(c.addedAt).toLocaleDateString()}</div>
                        </div>
                        <button onClick={() => handleRemoveContact(c.id)} style={{ background: "none", border: "1px solid #ef444440", color: "#ef4444", borderRadius: 6, padding: "4px 10px", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, cursor: "pointer" }}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export function ShareModal({ investigation, onClose, ui }) {
  const { B, fs } = ui;
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    fetch("/api/contacts", { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d.contacts) setContacts(d.contacts); })
      .catch(() => {});
  }, []);

  const handleExport = async () => {
    try {
      const res = await fetch(`/api/investigations/${investigation.id}`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not export.");

      const fileName = `${(investigation.name || "investigation").replace(/\s+/g, "_")}.json`;
      const blob = new Blob([JSON.stringify(data.investigation, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setStatus(e.message);
    }
  };

  const handleShare = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setStatus("");

    try {
      const res = await fetch(`/api/investigations/${investigation.id}/share`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Share failed.");
      setStatus("Shared successfully!");
      setEmail("");
      setMessage("");
    } catch (e) {
      setStatus(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 16, padding: 32, width: 440, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: fs(16), color: B.text }}>Share Investigation</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(18) }}>x</button>
        </div>
        <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 20 }}>{investigation.name}</div>

        <button onClick={handleExport} style={{ width: "100%", marginBottom: 20, padding: "10px 16px", borderRadius: 8, background: B.bgPanel, border: `1px solid ${B.border}`, color: B.text, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: fs(13), textAlign: "left" }}>
          Export as JSON
        </button>

        <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 20 }}>
          <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 12 }}>Share with Idle Hour User</div>
          {contacts.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {contacts.map(c => (
                <button key={c.id} onClick={() => setEmail(c.contactEmail)} style={{ padding: "4px 12px", borderRadius: 999, fontSize: fs(11), fontWeight: 600, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", background: email === c.contactEmail ? B.primary : B.bgPanel, color: email === c.contactEmail ? "#fff" : B.textMuted, border: `1px solid ${email === c.contactEmail ? B.primary : B.border}` }}>
                  {c.contactEmail}
                </button>
              ))}
            </div>
          )}
          <input
            type="email"
            placeholder="Recipient email address..."
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleShare()}
            style={{ width: "100%", marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", fontSize: fs(13), boxSizing: "border-box", outline: "none" }}
          />
          <textarea
            placeholder="Message (optional)..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={2}
            style={{ width: "100%", marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", fontSize: fs(13), resize: "vertical", boxSizing: "border-box", outline: "none" }}
          />
          <button onClick={handleShare} disabled={loading || !email.trim()} style={{ width: "100%", padding: "10px 16px", borderRadius: 8, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, cursor: loading || !email.trim() ? "not-allowed" : "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: fs(13), opacity: loading || !email.trim() ? 0.55 : 1 }}>
            {loading ? "Sharing..." : "Share"}
          </button>
          {status && <div style={{ marginTop: 10, fontSize: fs(12), color: status.toLowerCase().includes("success") ? "#16a34a" : "#ef4444" }}>{status}</div>}
        </div>
      </div>
    </div>
  );
}

export function MyBundlesPage({
  user,
  bundles = [],
  investigations = [],
  receivedInvestigations = [],
  onNavigate,
  onLogin,
  onLogout,
  onLoad,
  onLoadInvestigation,
  onRename,
  onRenameInvestigation,
  onDelete,
  onDeleteInvestigation,
  onShare,
  onLoadReceived,
  onDismissReceived,
  pcapSession,
  collapsed,
  onToggleCollapse,
  ui,
}) {
  const { B, fs, HP, AppShell } = ui;
  const cardStyle = { padding: 18, borderRadius: 12, background: B.bgCard, border: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 };
  const dangerButton = { ...HP.navBtn, color: "#ef4444", borderColor: "#ef4444" };
  const tagStyle = { fontSize: fs(11), color: B.text, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 999, padding: "4px 10px" };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="bundles" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, color: B.text, fontFamily: "'Satoshi', sans-serif", padding: 40 }}>
      {ui.GroupTabs && <ui.GroupTabs page="bundles" onNavigate={onNavigate} style={{ margin: "-40px -40px 32px", position: "relative" }} />}
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <h1 style={{ fontSize: fs(28), fontWeight: 900, marginBottom: 12 }}>Saved bundles and investigations</h1>
        <p style={{ color: B.textMuted, marginBottom: 24 }}>Your saved STIX bundles and investigations are stored privately for your account. Open, rename, or delete anything from here.</p>
        <div style={{ display: "grid", gap: 24 }}>
          <section>
            <div style={{ fontSize: fs(14), fontWeight: 700, marginBottom: 12 }}>Saved Bundles</div>
            {bundles.length === 0 ? (
              <div style={{ padding: 24, borderRadius: 12, background: B.bgCard, border: `1px solid ${B.border}`, color: B.textMuted }}>No saved bundles yet. Save a bundle from the viewer to keep it here.</div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {bundles.map((bundle) => (
                  <div key={bundle.id} style={cardStyle}>
                    <div>
                      <div style={{ fontWeight: 700, color: B.text }}>{bundle.name}</div>
                      <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 4 }}>{new Date(bundle.createdAt).toLocaleString()}</div>
                      {bundle.eventName && <div style={{ fontSize: fs(11), marginTop: 6 }}><strong>Event:</strong> {bundle.eventName}</div>}
                      {bundle.tags?.length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {bundle.tags.map((tag) => (
                            <span key={tag} style={tagStyle}>{tag}</span>
                          ))}
                        </div>
                      )}
                      {bundle.enrichment?.riskScore != null && (
                        <div style={{ marginTop: 6, fontSize: fs(11), color: B.accent }}>
                          Threat score: {bundle.enrichment.riskScore} - {bundle.enrichment.totalObjects} objects
                        </div>
                      )}
                      {bundle.classification && (
                        <div style={{ marginTop: 6, fontSize: fs(11), color: B.primary }}>
                          Classification: {bundle.classification}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button onClick={() => onLoad(bundle.id)} style={HP.primaryBtn}>Open</button>
                      <button onClick={() => onRename(bundle.id)} style={HP.navBtn}>Rename</button>
                      <button onClick={() => onDelete(bundle.id)} style={dangerButton}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div style={{ fontSize: fs(14), fontWeight: 700, marginBottom: 12 }}>Saved Investigations</div>
            {investigations.length === 0 ? (
              <div style={{ padding: 24, borderRadius: 12, background: B.bgCard, border: `1px solid ${B.border}`, color: B.textMuted }}>No saved investigations yet. Use Save Investigation to persist your workspace and dashboard state.</div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {investigations.map((item) => (
                  <div key={item.id} style={cardStyle}>
                    <div>
                      <div style={{ fontWeight: 700, color: B.text }}>{item.name}</div>
                      <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 4 }}>{new Date(item.createdAt).toLocaleString()}</div>
                      {item.notes && <div style={{ fontSize: fs(11), marginTop: 6, color: B.textMuted }}>{item.notes}</div>}
                      {item.tags?.length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {item.tags.map((tag) => (
                            <span key={tag} style={tagStyle}>{tag}</span>
                          ))}
                        </div>
                      )}
                      {item.classification && (
                        <div style={{ marginTop: 6, fontSize: fs(11), color: B.primary }}>
                          Classification: {item.classification}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button onClick={() => onLoadInvestigation(item.id)} style={HP.primaryBtn}>Load</button>
                      <button onClick={() => onShare(item)} style={{ ...HP.navBtn, color: B.accent, borderColor: B.accent }}>Share</button>
                      <button onClick={() => onRenameInvestigation(item.id)} style={HP.navBtn}>Rename</button>
                      <button onClick={() => onDeleteInvestigation(item.id)} style={dangerButton}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div style={{ fontSize: fs(14), fontWeight: 700, marginBottom: 12 }}>Shared with Me</div>
            {receivedInvestigations.length === 0 ? (
              <div style={{ padding: 24, borderRadius: 12, background: B.bgCard, border: `1px solid ${B.border}`, color: B.textMuted }}>No investigations have been shared with you yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {receivedInvestigations.map((item) => (
                  <div key={item.id} style={{ ...cardStyle, border: `1px solid ${B.accent}33` }}>
                    <div>
                      <div style={{ fontWeight: 700, color: B.text }}>{item.name}</div>
                      <div style={{ fontSize: fs(11), color: B.accent, marginTop: 3 }}>Shared by {item.ownerEmail}</div>
                      <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 2 }}>{new Date(item.shared_at).toLocaleString()}</div>
                      {item.message && <div style={{ fontSize: fs(11), marginTop: 6, color: B.textMuted, fontStyle: "italic" }}>"{item.message}"</div>}
                      {item.classification && <div style={{ marginTop: 6, fontSize: fs(11), color: B.primary }}>Classification: {item.classification}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button onClick={() => onLoadReceived(item.id)} style={HP.primaryBtn}>Load</button>
                      <button onClick={() => onDismissReceived(item.id)} style={dangerButton}>Dismiss</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
      </div>
    </div>
  );
}
